import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api, money } from "@/lib/api";

export default function CrmInvoicePrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/crm/invoices/${id}`).then(({ data }) => setData(data)).catch(() => setError("Invoice not found"));
  }, [id]);

  if (error) return <div className="min-h-screen bg-white text-black p-10 font-mono text-sm">{error}</div>;
  if (!data) return <div className="min-h-screen bg-white text-black p-10 font-mono text-sm">Loading invoice...</div>;

  const { invoice: inv, load, carrier } = data;

  return (
    <div className="min-h-screen bg-neutral-100 py-8 px-4" data-testid="invoice-print-page">
      <div className="no-print max-w-3xl mx-auto mb-4 flex justify-between items-center">
        <a href="/admin/crm/invoices" data-testid="invoice-print-back" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-600 hover:text-black">
          <ArrowLeft size={13} /> Back to invoices
        </a>
        <button
          data-testid="invoice-print-button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-black text-white font-mono text-[10px] uppercase tracking-[0.2em] px-5 py-3 hover:bg-neutral-800"
        >
          <Printer size={13} /> Print / Save PDF
        </button>
      </div>

      <div className="max-w-3xl mx-auto bg-white text-black shadow-lg print:shadow-none" data-testid="invoice-sheet">
        <div className="bg-black text-white px-8 py-6 flex justify-between items-center">
          <div>
            <img src="/logo.png" alt="Purpose Haul Dispatch" className="h-10 w-auto mb-2 bg-white px-2 py-1" />
            <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400">Driven With Purpose — Charlotte, NC</p>
          </div>
          <div className="text-right">
            <p className="font-display font-extrabold text-3xl tracking-tight">INVOICE</p>
            <p className="font-mono text-xs text-neutral-400 mt-1" data-testid="invoice-print-no">{inv.invoice_no}</p>
          </div>
        </div>

        <div className="px-8 py-6 grid grid-cols-2 gap-6 border-b border-neutral-200">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Bill To</p>
            <p className="font-bold" data-testid="invoice-print-carrier">{carrier?.company}</p>
            <p className="text-sm text-neutral-600">{carrier?.owner_name}</p>
            {carrier?.mc_number && <p className="text-sm text-neutral-600">MC {carrier.mc_number}{carrier.dot_number ? ` · DOT ${carrier.dot_number}` : ""}</p>}
            {carrier?.email && <p className="text-sm text-neutral-600">{carrier.email}</p>}
            {carrier?.phone && <p className="text-sm text-neutral-600">{carrier.phone}</p>}
          </div>
          <div className="text-right">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Details</p>
            <p className="text-sm">Date: {new Date(inv.created_at).toLocaleDateString()}</p>
            <p className="text-sm">Load: {inv.load_no}</p>
            <p className="text-sm capitalize">Status: {inv.status}</p>
          </div>
        </div>

        {load && (
          <div className="px-8 py-6 border-b border-neutral-200">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Load Details</p>
            <table className="w-full text-sm">
              <tbody>
                <tr className="border-b border-neutral-100"><td className="py-2 text-neutral-500">Broker</td><td className="py-2 text-right">{load.broker}</td></tr>
                <tr className="border-b border-neutral-100"><td className="py-2 text-neutral-500">Route</td><td className="py-2 text-right">{load.pickup} → {load.delivery}</td></tr>
                {(load.pickup_date || load.delivery_date) && (
                  <tr className="border-b border-neutral-100"><td className="py-2 text-neutral-500">Dates</td><td className="py-2 text-right">{[load.pickup_date, load.delivery_date].filter(Boolean).join(" → ")}</td></tr>
                )}
                {load.miles && <tr className="border-b border-neutral-100"><td className="py-2 text-neutral-500">Miles</td><td className="py-2 text-right">{load.miles}{load.rpm ? ` · $${load.rpm}/mi` : ""}</td></tr>}
                <tr><td className="py-2 text-neutral-500">Gross Load Rate</td><td className="py-2 text-right">{money(load.gross_rate)}</td></tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="px-8 py-6 border-b border-neutral-200">
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Charges</p>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-neutral-100">
                <td className="py-2">Dispatch service fee — {inv.fee_pct}% of {money(load?.gross_rate ?? 0)} gross</td>
                <td className="py-2 text-right font-semibold" data-testid="invoice-print-amount">{money(inv.amount)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {inv.payments.length > 0 && (
          <div className="px-8 py-6 border-b border-neutral-200">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mb-3">Payments Received</p>
            <table className="w-full text-sm">
              <tbody>
                {inv.payments.map((p) => (
                  <tr key={p.id} className="border-b border-neutral-100">
                    <td className="py-2">{new Date(p.date).toLocaleDateString()} — {p.method || "Payment"}{p.note ? ` (${p.note})` : ""}</td>
                    <td className="py-2 text-right">-{money(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="px-8 py-6 flex justify-between items-center bg-neutral-50">
          <div>
            {inv.square_link && (
              <>
                <a
                  href={inv.square_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="invoice-print-square-link"
                  className="inline-block bg-black text-white font-mono text-[10px] uppercase tracking-[0.2em] px-5 py-3"
                >
                  Pay Online with Square
                </a>
                <p className="text-[11px] text-neutral-500 mt-2 break-all">{inv.square_link}</p>
              </>
            )}
          </div>
          <div className="text-right">
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">Balance Due</p>
            <p className="font-display font-extrabold text-4xl" data-testid="invoice-print-balance">{money(inv.balance)}</p>
          </div>
        </div>

        <div className="px-8 py-5 text-[10px] text-neutral-500 leading-relaxed">
          Purpose Haul Dispatch — Dispatch and administrative support provided under your signed service agreement.
        </div>
      </div>
    </div>
  );
}
